import {
    useContext,
    useEffect,
    useState,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    Animated,
    View
} from "react-native";
import type IAspectRatioProps from "./type";
import stylesheet, {
    getAspectRatioStateHeight,
    getAspectRatioSourceKey,
    getAspectRatioStatus,
    getAspectRatioRadius,
    getAspectRatioValue,
    useStyles
} from "./stylesheet";
import AspectRatioState from "./internal/state";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useAnimatedState
} from "../../helpers/motion";
import {
    ImageOff as ImageOffIcon,
    Image as ImageIcon,
    Lock as LockIcon
} from "lucide-react-native";
import {
    CardContext
} from "../card/context";
import Skeleton from "../skeleton";

const AspectRatio: FC<IAspectRatioProps> = ({
    ratio: ratioProp = "16:9",
    "aria-label": ariaLabel,
    isRestricted = false,
    onError: onErrorProp,
    objectFit = "cover",
    radiusRole = "auto",
    onLoad: onLoadProp,
    flushEdge = "top",
    isLoading = false,
    customLocalize,
    renderFallback,
    customTheme,
    children,
    onRetry,
    source,
    style,
    alt,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const cardContext = useContext(CardContext);

    const [
        isImageLoaded,
        setIsImageLoaded
    ] = useState<boolean>(false);

    const [
        isImageFailed,
        setIsImageFailed
    ] = useState<boolean>(false);

    const [
        attempt,
        setAttempt
    ] = useState<number>(0);

    const [
        frameHeight,
        setFrameHeight
    ] = useState<number>(0);

    const opacity = useAnimatedState({
        value: isImageLoaded ? 1 : 0,
        intent: "surfaceEnter",
        motion: "fade",
        customTheme
    });

    const sourceKey = getAspectRatioSourceKey({
        source
    });

    const ratio = getAspectRatioValue({
        ratio: ratioProp
    });

    const borderRadius = getAspectRatioRadius({
        cardInset: cardContext?.inset,
        radiusRole,
        radiuses,
        borders
    });

    const hasChildren = children !== undefined && children !== null && children !== false;

    const status = getAspectRatioStatus({
        isFailed: isImageFailed && !hasChildren,
        hasContent: hasChildren || !!source,
        isRestricted,
        isLoading
    });

    const stateHeight = getAspectRatioStateHeight({
        hasDescription: status === "restricted" || (status === "failed" && !!alt),
        hasAction: status === "failed" && !!onRetry && !renderFallback,
        controlHeights,
        typography,
        iconSizes,
        spaces
    });

    const isStateStatus = status !== "ready" && status !== "loading";

    const isIconOnly = isStateStatus && (radiusRole === "form" || (frameHeight > 0 && frameHeight < stateHeight));

    const {
        container: containerDynamicStyle
    } = useStyles({
        borderRadius,
        shapeConfigs,
        radiusRole,
        flushEdge,
        colors,
        ratio
    });

    useEffect(() => {
        setIsImageLoaded(false);

        setIsImageFailed(false);
    }, [
        sourceKey
    ]);

    const onLoad = () => {
        setIsImageLoaded(true);

        onLoadProp?.();
    };

    const onError = () => {
        setIsImageFailed(true);

        onErrorProp?.();
    };

    const handleLayout = (event: LayoutChangeEvent) => {
        setFrameHeight(Math.round(event.nativeEvent.layout.height));
    };

    const handleRetry = () => {
        setIsImageFailed(false);

        setIsImageLoaded(false);

        setAttempt((current) => current + 1);

        onRetry?.();
    };

    const renderImage = () => {
        if(!source) {
            return null;
        }

        return <Animated.Image
            aria-hidden={alt ? undefined : true}
            aria-label={alt || undefined}
            resizeMode={objectFit}
            style={[
                stylesheet.fill,
                {
                    opacity
                }
            ]}
            onError={onError}
            onLoad={onLoad}
            source={source}
            key={attempt}
        />;
    };

    const renderSkeleton = () => {
        return <Skeleton
            customTheme={customTheme}
            aspectRatio={ratio}
            animation="pulse"
            radius="none"
        />;
    };

    const renderContent = () => {
        if(status === "restricted") {
            return <AspectRatioState
                description={localize("ask-owner-for-access")}
                title={localize("no-view-permission")}
                customLocalize={customLocalize}
                customTheme={customTheme}
                isIconOnly={isIconOnly}
                onRetry={handleRetry}
                icon={LockIcon}
            />;
        }

        if(status === "loading") {
            return renderSkeleton();
        }

        if(status === "failed") {
            return renderFallback ? renderFallback() : <AspectRatioState
                title={localize("image-load-failed")}
                customLocalize={customLocalize}
                description={alt || undefined}
                customTheme={customTheme}
                isIconOnly={isIconOnly}
                onRetry={handleRetry}
                isAction={!!onRetry}
                icon={ImageOffIcon}
            />;
        }

        if(status === "empty") {
            return <AspectRatioState
                title={localize("image-not-added")}
                customLocalize={customLocalize}
                customTheme={customTheme}
                isIconOnly={isIconOnly}
                onRetry={handleRetry}
                icon={ImageIcon}
            />;
        }

        if(hasChildren) {
            return <View
                style={stylesheet.fill}
            >
                {children}
            </View>;
        }

        return [
            isImageLoaded ? null : <View
                style={stylesheet.fill}
                key="skeleton"
            >
                {renderSkeleton()}
            </View>,
            renderImage()
        ];
    };

    const isBusy = status === "loading" || (status === "ready" && !hasChildren && !isImageLoaded);

    return <View
        {...props}
        aria-label={status === "loading" ? localize("loading") : ariaLabel}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        onLayout={handleLayout}
        aria-busy={isBusy}
    >
        {renderContent()}
    </View>;
};
export default AspectRatio;
